/**
 * Hook that returns a *stable* AntD ``components`` prop for ``<Table>``.
 *
 * Why this exists: AntD treats ``components.body.row`` as a fresh
 * component on every render, so the naive
 * ``components={{ body: { row: (props) => <DraggableRow ... /> } }}``
 * pattern remounts every ``<tr>`` on every parent re-render. During a
 * drag, that detaches the source row from the DOM mid-gesture and the
 * HTML5 drag-and-drop contract cancels the drag.
 *
 * The fix: ``useMemo`` the components object once (empty deps) and feed
 * per-row data through mutable refs that update in place each render.
 * Consumers pass refs to ``rows`` and ``handlers``; the row component
 * reads them at render time via ``ref.current``.
 *
 * Usage (catalog, with DnD):
 *   const components = useStableTableComponents({
 *     rowsRef,
 *     handlersRef,
 *     renderRow: (row, handlers, rowProps) =>
 *       <DraggableRow row={row} ...handlers rowProps={rowProps} />,
 *   });
 *
 * Usage (stock, no DnD): omit ``renderRow`` and the hook returns
 * ``undefined`` so the table falls back to AntD's default body row.
 */

import { useMemo, type MutableRefObject, type ReactElement } from "react";

export type StableRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  "data-row-key"?: string;
  children?: React.ReactNode;
};

export interface UseStableTableComponentsOptions<TRow, THandlers> {
  /** Ref whose ``.current`` is the current row list (keyed by ``.key``). */
  rowsRef: MutableRefObject<readonly TRow[]>;
  /** Ref whose ``.current`` holds the callbacks the row needs. */
  handlersRef: MutableRefObject<THandlers>;
  /**
   * Renderer for each table row. Receives the matched row (or undefined
   * if no match), the current handlers snapshot, and the raw AntD
   * row props (including ``data-row-key`` and ``children``). Optional —
   * when omitted, the hook returns ``undefined`` so the table uses
   * AntD's default body row.
   */
  renderRow?: (
    row: TRow | undefined,
    handlers: THandlers,
    rowProps: StableRowProps,
  ) => ReactElement;
  /**
   * Equality function for matching a row to its ``data-row-key``.
   * Defaults to ``row.key === dataRowKey``.
   */
  matchRow?: (row: TRow, dataRowKey: string | undefined) => boolean;
}

export function useStableTableComponents<
  TRow extends { key: string },
  THandlers,
>(options: UseStableTableComponentsOptions<TRow, THandlers>) {
  const { rowsRef, handlersRef, renderRow, matchRow } = options;

  return useMemo(() => {
    if (!renderRow) {
      // No renderer → no override; AntD uses its default body.row.
      return undefined;
    }
    const match =
      matchRow ?? ((r: TRow, key: string | undefined) => r.key === key);
    // The function-component identity below is captured once here (empty
    // deps). Fresh row data flows in via ``rowsRef.current`` and fresh
    // handlers via ``handlersRef.current`` — both mutate in place each
    // render.
    const RowComponent = (props: StableRowProps) => {
      const rowKey = props["data-row-key"];
      const row = rowsRef.current.find((r) => match(r, rowKey));
      return renderRow(row, handlersRef.current, props);
    };
    return { body: { row: RowComponent } };
    // Empty deps: every value flowing into the row component comes from
    // refs that mutate in place each render. The component identity
    // stays stable across parent re-renders → AntD doesn't remount rows
    // mid-drag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
